import {
  Button,
  Flex,
  Input,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {
  useCreateEmployee,
  useEmployees,
  useToggleEmployeeState,
  useUpdateEmployee,
  useUpdateEmployeeRemittanceInfo, // 新增導入
} from '../../utils/api-hooks/employee.hook';
import {
  AccountBookOutlined,
  EditOutlined,
  SearchOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import useModal from 'antd/es/modal/useModal';
import EmployeeFormComponent from './employees-form.component';
import { useRef, useState } from 'react';
import { FormInstance } from 'antd/lib';
import { usePagination } from '../../shared/usePagination';
import {
  EMPLOYEE_STATE,
  EMPLOYEE_STATE_COLOR,
  EMPLOYEE_STATE_LABEL,
} from '../../constants/employee';
import { find, map } from 'lodash';
import { Link } from 'react-router-dom';
import AddEmployeeFormComponent from '../departments/department/add-employee-form.component';
import { useAddDepartmentEmployee } from '../../utils/api-hooks/department.hook';
import {
  FindEmployeesResult,
  ToggleEmployeeStateDtoStateEnum,
} from '../../utils/api-client';
import dayjs from 'dayjs';
import { EmployeeRemittanceForm } from './employee-remittance-form';
import { banks } from '../../constants/bank';
import { EmployeeImportForm } from './employee-import-form';

export default function EmployeesContainer() {
  const remittanceForm = useRef<
    FormInstance<{
      bankCode: string;
      bankAccount: string;
    }>
  >(null);
  const [modal, contextHolder] = useModal();
  const createFormRef = useRef<FormInstance>(null);
  const editFormRef = useRef<FormInstance>(null);
  const addEmployeeFormRef = useRef<FormInstance>(null);
  const pagination = usePagination();
  const [keyword, setKeyword] = useState<string>('');
  const { data: { data: employees, total } = {}, isLoading } = useEmployees({
    take: pagination.take,
    skip: pagination.skip,
    keyword,
  });
  const { mutateAsync: createEmployee } = useCreateEmployee();
  const { mutateAsync: updateEmployee } = useUpdateEmployee();
  const { mutateAsync: toggleEmployeeState } = useToggleEmployeeState();
  const { mutateAsync: addDepartmentEmployee } = useAddDepartmentEmployee();

  // 新增 useUpdateEmployeeRemittanceInfo hook
  const {
    mutateAsync: updateEmployeeRemittanceInfo,
    isLoading: isUpdatingRemittance,
  } = useUpdateEmployeeRemittanceInfo();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onKeywordChange = (value: string) => {
    setKeyword(value);
    if (pagination) pagination.onChange(1);
  };

  const onCreateEmployeeButtonClick = async () => {
    let id: string;

    modal
      .confirm({
        title: '新增員工',
        content: <EmployeeFormComponent formRef={createFormRef} />,
        icon: null,
        async onOk() {
          const values = await createFormRef.current!.validateFields();

          const res = await createEmployee(values);

          id = res.data.id;
        },
      })
      .then(
        (res) => {
          if (!res) {
            return;
          }

          modal.confirm({
            title: '加入部門',
            content: (
              <AddEmployeeFormComponent
                formRef={addEmployeeFormRef}
                employeeId={id}
                isAfterEmployeeCreated
              />
            ),
            icon: null,
            async onOk(...args) {
              const values = await addEmployeeFormRef.current!.validateFields();

              await addDepartmentEmployee(values);
            },
          });
        },
        () => {}
      );
  };

  const onImportEmployeeButtonClick = () => {
    modal.info({
      title: '匯入員工資料',
      content: <EmployeeImportForm />,
      icon: null,
      okText: '取消',
    });
  };

  const onEditEmployeeButtonClick = (
    record: FindEmployeesResult['data'][number]
  ) => {
    modal.confirm({
      title: '編輯員工',
      content: (
        <EmployeeFormComponent
          formRef={editFormRef}
          initialValues={{
            name: record.name,
            phone: record.phone,
            birthday: record.birthday ? dayjs(record.birthday) : undefined,
            idNumber: record.idNumber,
            nationalIdNumber: record.nationalIdNumber,
            email: record.email,
            onBoardingTime: dayjs(record.onBoardingTime),
            type: record.type,
          }}
        />
      ),
      icon: null,
      async onOk() {
        const values = await editFormRef.current!.validateFields();
        console.log(values);

        await updateEmployee({
          id: record.id,
          ...values,
        });
      },
    });
  };

  const onEditEmployeeRemittanceButtonClick = (
    record: FindEmployeesResult['data'][number]
  ) => {
    modal.confirm({
      title: `編輯 ${record.name} 的匯款資訊`,
      content: (
        <EmployeeRemittanceForm
          formRef={remittanceForm}
          initialValues={record.remittanceInfo}
        />
      ),
      icon: null,
      width: 600,
      okText: '更新匯款資訊',
      cancelText: '取消',
      async onOk() {
        try {
          const values = await remittanceForm.current!.validateFields();

          await updateEmployeeRemittanceInfo({
            id: record.id,
            bankCode: values.bankCode,
            bankAccount: values.bankAccount,
          });
        } catch (error) {
          console.error('更新匯款資訊失敗:', error);
          throw error; // 重新拋出錯誤，讓 modal 保持開啟狀態
        }
      },
    });
  };

  const handleToggleEmployeeState = async (
    id: string,
    currentState: string
  ) => {
    const newState =
      currentState === EMPLOYEE_STATE.ACTIVATED
        ? ToggleEmployeeStateDtoStateEnum.Suspended
        : ToggleEmployeeStateDtoStateEnum.Activated;

    await toggleEmployeeState({
      id,
      state: newState,
    });
  };

  return (
    <Space direction="vertical" className="w-full">
      {contextHolder}
      <div className="text-2xl font-bold">員工管理</div>
      <div className="flex items-center p-4 rounded gap-4 bg-gray-50">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">關鍵字:</span>
          <Input.Search
            placeholder="輸入姓名或電話號碼進行搜尋"
            allowClear
            style={{ width: 300 }}
            onSearch={onKeywordChange}
            enterButton={<SearchOutlined />}
          />
        </div>
      </div>
      <Table
        dataSource={employees}
        rowKey="id"
        size="small"
        loading={isLoading}
        scroll={{ x: 'max-content' }}
        pagination={{
          ...pagination,
          total,
        }}
        title={() => (
          <div className="flex gap-2">
            <Button type="primary" onClick={onCreateEmployeeButtonClick}>
              新增員工
            </Button>
            <Button onClick={onImportEmployeeButtonClick}>匯入員工資料</Button>
          </div>
        )}
        columns={[
          {
            align: 'center',
            dataIndex: 'state',
            title: '狀態',
            width: 70,
            render(value) {
              return (
                <span className={EMPLOYEE_STATE_COLOR[value as EMPLOYEE_STATE]}>
                  {EMPLOYEE_STATE_LABEL[value as EMPLOYEE_STATE]}
                </span>
              );
            },
          },
          {
            align: 'center',
            dataIndex: 'lineId',
            title: '是否已綁定 LINE',
            width: 130,
            render(value) {
              return value ? (
                <Tag color="green">已綁定</Tag>
              ) : (
                <Tag color="red">未綁定</Tag>
              );
            },
          },
          {
            align: 'center',
            dataIndex: 'onBoardingTime',
            title: '入職時間',
            width: 120,
            render(value) {
              return dayjs(value).format('YYYY/MM/DD');
            },
          },
          {
            title: '姓名',
            width: 80,
            dataIndex: 'name',
          },
          {
            dataIndex: 'birthday',
            title: '生日',
            width: 120,
            render(value) {
              return dayjs(value).format('YYYY/MM/DD');
            },
          },
          {
            title: '電話號碼',
            width: 120,
            dataIndex: 'phone',
          },
          {
            title: '電子郵件',
            dataIndex: 'email',
            width: 250,
            render(value) {
              return value || '---';
            },
          },
          // 新增匯款資訊欄位
          {
            title: '匯款資訊',
            dataIndex: 'remittanceInfo',
            width: 200,
            render(v, record) {
              const bank = find(banks, [
                'bankCode',
                record.remittanceInfo.bankCode,
              ]);

              return (
                <Flex vertical>
                  <span>
                    {bank?.bankCode} - {bank?.bankName}
                  </span>
                  <span>{record.remittanceInfo.bankAccount}</span>
                </Flex>
              );
            },
          },
          {
            title: '部門',
            dataIndex: 'departments',
            width: 120,
            render(value) {
              return map(value, (department) => (
                <Link
                  to={`/human-resource/departments/${department.id}`}
                  key={department.id}
                >
                  <Tag>{department.name}</Tag>
                </Link>
              ));
            },
          },
          {
            title: '操作',
            width: 120,
            dataIndex: 'operation',
            render(_, record) {
              return (
                <Space>
                  <Tooltip title="編輯">
                    <Button
                      icon={<EditOutlined />}
                      shape="circle"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditEmployeeButtonClick(record);
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="編輯匯款資訊">
                    <Button
                      icon={<AccountBookOutlined />}
                      shape="circle"
                      size="small"
                      loading={isUpdatingRemittance}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditEmployeeRemittanceButtonClick(record);
                      }}
                    />
                  </Tooltip>
                  <Popconfirm
                    title={`確定要將員工狀態從「${EMPLOYEE_STATE_LABEL[record.state as EMPLOYEE_STATE]}」切換為「${EMPLOYEE_STATE_LABEL[record.state === EMPLOYEE_STATE.ACTIVATED ? EMPLOYEE_STATE.SUSPENDED : EMPLOYEE_STATE.ACTIVATED]}」嗎？`}
                    onConfirm={() =>
                      handleToggleEmployeeState(record.id, record.state)
                    }
                    okText="確定"
                    cancelText="取消"
                  >
                    <Button
                      icon={<UserSwitchOutlined />}
                      shape="circle"
                      size="small"
                      type={
                        record.state === EMPLOYEE_STATE.ACTIVATED
                          ? 'default'
                          : 'primary'
                      }
                    />
                  </Popconfirm>
                </Space>
              );
            },
          },
        ]}
      />
    </Space>
  );
}
